import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Sector } from 'recharts';
import { chartTheme } from './chartTheme';
import { YChartTooltip } from './YChartTooltip';

/**
 * Reusable stats pie for categorical breakdowns (e.g., time of day, weather).
 * - Uses shared chart theme and tooltip for consistency with line charts.
 * - Shows only labels in the legend (no numbers); percentages appear in tooltip.
 * - Active slice floats up (larger radius) on hover.
 */
export function StatsPie({
  title,
  data,
  nameKey = 'name',
  valueKey = 'value',
  loading = false,
  height = 240,
}) {
  const [activeIndex, setActiveIndex] = useState(-1);

  const pieData = useMemo(() => {
    return (Array.isArray(data) ? data : [])
      .map((row) => ({
        name: (row?.[nameKey] ?? '').toString(),
        value: Number(row?.[valueKey] ?? 0),
      }))
      .filter((row) => row.name && !Number.isNaN(row.value) && row.value > 0);
  }, [data, nameKey, valueKey]);

  const total = useMemo(
    () => pieData.reduce((sum, row) => sum + (Number.isFinite(row.value) ? row.value : 0), 0),
    [pieData]
  );

  const hasData = total > 0 && pieData.length > 0;

  const valueFormatter = useCallback(
    (val, name, entry) => {
      const pct = total > 0 ? (100 * (val || 0)) / total : 0;
      const whole = Math.round(val || 0);
      return `${pct.toFixed(1)}% (${whole} deer)`;
    },
    [total]
  );

  const labelFormatter = useCallback((label) => (label ? label.replace('_', ' ') : ''), []);

  const onEnter = useCallback((_, idx) => setActiveIndex(idx), []);
  const onLeave = useCallback(() => setActiveIndex(-1), []);

  return (
    <div className="y-stats-pie">
      {title && <p className="y-chart-title">{title}</p>}
      {hasData ? (
        <>
          <div className="y-stats-pie__plot" style={{ height }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={0}
                  cornerRadius={4}
                  stroke="var(--color-surface, #ffffff)"
                  strokeWidth={3}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  isAnimationActive={!loading}
                  onMouseEnter={onEnter}
                  onMouseLeave={onLeave}
                  activeIndex={activeIndex}
                  activeShape={(props) => {
                    const { outerRadius: r, innerRadius, ...rest } = props;
                    return (
                      <Sector
                        {...rest}
                        innerRadius={innerRadius}
                        outerRadius={(r || 80) + 6}
                      />
                    );
                  }}
                >
                  {pieData.map((entry, idx) => (
                    <Cell
                      key={`${entry.name}-${idx}`}
                      fill={chartTheme.palette[idx % chartTheme.palette.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  cursor={false}
                  content={
                    <YChartTooltip
                      labelFormatter={labelFormatter}
                      valueFormatter={valueFormatter}
                    />
                  }
                  wrapperStyle={{ transition: 'none' }}
                  allowEscapeViewBox={{ x: true, y: true }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="y-chart-legend">
            {pieData.map((entry, idx) => (
              <li key={`${entry.name}-${idx}`} className="y-chart-legend__item">
                <span
                  aria-hidden
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: chartTheme.palette[idx % chartTheme.palette.length],
                  }}
                />
                <span className="text-capitalize">{entry.name.replace('_', ' ')}</span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <div className="y-chart-empty">No data yet.</div>
      )}
    </div>
  );
}

StatsPie.propTypes = {
  title: PropTypes.string,
  data: PropTypes.array,
  nameKey: PropTypes.string,
  valueKey: PropTypes.string,
  loading: PropTypes.bool,
  height: PropTypes.number,
};

export default StatsPie;






